import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { isOrderClosed } from '../../utils/order';
import { zCancelledReason, zOrderStatus, zPlatform, zServiceType } from '../enums';
import { assertTypes, type NoNewFields, zPrice } from '../primitives';
import { orderItemSchema, orderItemStrictSchema } from './orderItemSchema';
import { paymentSchema, paymentStrictSchema } from './paymentSchema';

// 1. Base Schema - Form initialization (shape + defaults only)
// Only main primitives: z.string(), z.number(), z.array(), etc.
// No strict validations like .min(), .int(), .guid()
// Also stored in `ordersArchived`, which is never migrated: read `archivedOrderSchema.ts` before
// removing a field, narrowing a type, or adding a field without a `.default()`.
export const orderSchema = z.object({
  /** TODO: Rename to `cancelReason` to avoid confusion.
   *    American English: canceledReason (one L)
   *    British English: cancelledReason (two Ls)
   * */
  cancelledReason: zCancelledReason.nullable().default(null),
  confirmationToken: z.string().nullable().default(null),
  confirmedAt: z.number().nullable().default(null),
  customerEmail: z.string().nullable().default(null), // TODO: Consider adding .email validation? Add to `orderPlatformSchema`?
  /** `customerName` represents the name for the order. Defaults to `restaurantCustomer.name`, but can be overridden during order checkout. **/
  customerName: z.string().nullable().default(null),
  customerPhoneNumber: z.string().nullable().default(null), // TODO: Add validation to ensure international format. Ie +61.
  deliveryAddress: z.string().nullable().default(null),
  deliveryNotes: z.string().nullable().default(null),
  deliverySuburbFee: z.number().nullable().default(null),
  deliverySuburbName: z.string().nullable().default(null),
  deliverySuburbPostcode: z.string().nullable().default(null),
  deliverySuburbUuid: z.string().nullable().default(null),
  discountCode: z.string().nullable().default(null).optional(),
  discountPercent: z.number().nullable().default(null),
  /**
   * `order.discountTotal` only represents order level discounts.
   * `calcAllDiscounts()` can be used to also include order item discounts.
   **/
  discountTotal: z.number().default(0),
  /** Null on every `orders` row; on every `ordersArchived` row, the closure that settled it. */
  registerClosureId: z.string().nullable().default(null),
  estWaitTime: z.number().nullable().default(null),
  /**
   * The time at which the standalone printer fetched the order.
   * Or, the time the order was successfully fetched by the POS terminal (and new order alert shown).
   * (only applies to online orders)
   */
  fetchedAt: z.number().nullable().default(null),
  fullyPaidAt: z.number().nullable().default(null),
  legacyRestaurantId: z.number().nullable().default(null),
  /**
   * `localCreatedAt` = when the order's originating system first recorded it.
   * - POS orders: set by the POS at first local save (enforced non-null for `platform: 'POS'`
   *   in `orderPlatformSchema`), and never overwritten — including for online orders reaching
   *   the device.
   * - Online orders: set by the server at submit (consumer recent-orders filtering depends on it).
   *
   * This field is NOT set when creating a new order in memory. `businessDate`, closure stamping
   * and the archive all key off it.
   */
  localCreatedAt: z.number().nullable().default(null),
  /**
   * - Only the primary POS is responsible for generating the local order id, using `nextLocalOrderId` from store.
   * - Secondary POS terminal can generate an order, but with `localOrderId` set to 0.
   * - The primary POS will then generate a `localOrderId`, once they are synced on primary POS.
   * - The `nextLocalOrderId` persists to local storage so that it can be maintained across app restarts.
   * - For standalone printers, `standalonePrinterNextLocalOrderId` from PlatformGlobals is used.
   */
  localOrderId: z.number().nullable().default(null),
  /**
   * `localUpdatedAt` is a timestamp representing when the POS last committed this order to its local storage.
   *
   * This field is ONLY updated when the order is saved to local storage via `saveOrder()`.
   *
   * This field is NOT updated:
   * - During in-memory cart modifications (adding items, changing quantities, etc.)
   * - When initialising a new in-memory order
   * - For non-POS orders. It always remains `null` if `order.platform` !== 'POS'
   *
   * Use cases:
   * - Determining sync conflicts between POS and server versions
   * - Identifying which orders have been committed to storage
   * - Tracking when orders were last persisted locally
   *
   * Note: Use `order.hasUnsavedChanges` to detect uncommitted cart changes.
   */
  localUpdatedAt: z.number().nullable().default(null),
  /**
   * @deprecated Use `lastProcessedByPrimaryPosAt`. Kept until no POS runs a bundle that reads or
   * writes only this flag: until then it is the authority, and the server derives
   * `lastProcessedByPrimaryPosAt` from it on every POS save (`orders/domain.savePosOrder`). The
   * POS no longer writes it, only sends it, derived from the stamp (`syncOrder`).
   *
   * Indicates whether the order was last saved by a secondary POS terminal.
   * - `true`: Last saved by secondary POS
   * - `false`: Last saved by primary POS
   * - `null`: Not a POS order (e.g., online orders)
   *
   * Used primarily to filter orders that need primary POS processing: the primary saves a
   * secondary's save again, which numbers it, prints what changed and sets this to `false`.
   */
  lastSavedBySecondaryPos: z.boolean().nullable().default(null).optional(), // Remove .optional() after a Convex migration
  /**
   * The save time (`localUpdatedAt`) of the primary POS's save that last numbered the order and
   * sent its kitchen docket. `null` while the order carries a save the primary has not processed:
   * a secondary POS's save. An online order no POS has saved (one the server timed out, say) is
   * `null` too, but never awaits the primary: it does not number or docket online orders this way.
   *
   * The primary finds the orders awaiting it through `posNative.getOrders` and saves each again, which
   * stamps this. Processing is idempotent: a number is assigned once, and a docket prints only the
   * difference from the items' print stamps. So a spare re-save costs nothing, and a lost docket
   * is what this guards against.
   *
   * Orders built outside the POS `saveOrder` (seeds, generated orders, test fixtures) stand for
   * orders the primary already saved, so they are stamped. Unstamped, the primary would print
   * them at startup.
   *
   * Rows that predate the field are `undefined` to the index and read back as `null`, so nothing
   * may read it until `m20261004_backfillOrderLastProcessedByPrimaryPosAt` has run. That covers
   * `orders` only: `ordersArchived` is never migrated, so its older rows keep `undefined`
   * (`archivedOrderCvxSchema`) and read back as `null`.
   *
   * TODO: Consider deriving "needs the primary" from the order instead (`needsNumberOrDocket`):
   *       the primary has work only while the order has no `localOrderId` or an item's qty is
   *       ahead of what the primary handled. That drops the merge rule that keeps this `null`
   *       (`mergeOrderVersions`) and the primary's re-save after a payment-only secondary save.
   *       It needs:
   *       - A per-item "qty the primary handled", merged forward like the print stamps. The
   *         print stamps can't serve: `lastPrintedAt` makes an item void-only and the next docket
   *         a change docket, and items no docket prints are never stamped.
   *       - The server storing the derived value on every order write, to index it.
   *       - A rule for a print job that fails to build (`PROCESSING_ERROR`), which the primary
   *         would otherwise retry on every refresh.
   *       Worth it only if those re-saves cause 409s in practice.
   */
  lastProcessedByPrimaryPosAt: z.number().nullable().default(null).optional(), // Remove .optional() after a Convex migration
  /** Formatted name of the passcode user who created this order (e.g., "ADAM W"). */
  createdBy: z.string().nullable().default(null).optional(),
  /**
   * Why the order price was adjusted — the shortcut's reason snapshot when a POS price adjustment
   * shortcut was applied (e.g. "STAFF", "SUNDAY"). Null for custom/coupon discounts and manual
   * surcharges.
   */
  priceAdjustmentReason: z.string().nullable().default(null).optional(),
  /** Formatted identity of who applied the order-level price adjustment (e.g. "ADAM W (STAFF)"). */
  priceAdjustmentAppliedBy: z.string().nullable().default(null).optional(),
  /**
   * Formatted identity of the manager who approved it. Null when no approval was needed
   * (surcharges never require approval).
   */
  priceAdjustmentApprovedBy: z.string().nullable().default(null).optional(),
  loyaltyCardFreeCoffeesRedeemed: z.number().nullable().default(null),
  loyaltyCardStampsEarned: z.number().nullable().default(null),
  notes: z.string().nullable().default(null),
  orderItems: z.array(orderItemSchema).default([]),
  orderItemsTotal: z.number().default(0),
  payments: z.array(paymentSchema).default([]),
  platform: zPlatform,
  /** `restaurantCustomerId` should be used in place of `customerAwsId` **/
  restaurantCustomerId: z.string().nullable().default(null),
  restaurantId: z.string(),
  scheduledJobIds: z.array(z.string()).default([]),
  serviceType: zServiceType.default('PICKUP'), // Ie takeaway order. TODO: Change default to 'NONE'?
  status: zOrderStatus,
  /**
   * When an order enters a "closed" state, it's a terminal/final state - no further status changes can be made.
   * An order is considered "closed" if its `status` is "CANCELLED" or "COMPLETED".
   * Order defaults to an "open" order, ie `isClosed` set to `false`.
   **/
  isClosed: z.boolean().default(false),
  submittedAt: z.number().nullable().default(null),
  surchargePercent: z.number().nullable().default(null),
  /**
   * `order.surchargeTotal` only represents order level surcharges.
   * `calcAllSurcharges()` can be used to also include order item surcharges.
   **/
  surchargeTotal: z.number().default(0),
  table: z.string().nullable().default(null),
  total: z.number().default(0),
  uuid: z.string().default(() => uuidv4()),
  voidedAt: z.number().nullable().default(null),
  /**
   * `version` will be `null` for client initiated orders. Once synced `version` will starts at 1.
   * This approach avoids comparing server timestamps with client timestamps (which avoids any epoch clock skew issues).
   * It also allow server originated order changes (eg payment.status) to be correctly synced to the POS.
   */
  version: z.number().nullable().default(null).optional(), // Remove .optional() after a Convex migration

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  /**
   * Timestamp of the last server-side save when the order's `version` number was incremented.
   * Applies to all order platform types.
   */
  updatedAt: z.number().optional(),
  /**
   * Trading date, `YYYY-MM-DD` in the restaurant's timezone. Derived from `localCreatedAt` by the
   * server at insert and never overwritten; reports filter on it directly. Clients never set it,
   * they only echo it back on sync.
   */
  businessDate: z.string().optional(),
  /**
   * Dead field, no longer written. Production rows carry it because an earlier deploy stamped it
   * at insert; `m20260904_unsetOrderSchemaVersion` unsets them, and the field is deleted from this
   * schema once that has run.
   */
  schemaVersion: z.number().optional(),
});
export type Order = z.output<typeof orderSchema>;

// 2. Form Submission Schema - Strict validations, no defaults
// .int(), .guid(), .email(), custom primitives (zText, zPrice) go here
// Never use .uuid(), use .guid() instead
export const orderStrictSchema = orderSchema
  .safeExtend({
    deliverySuburbFee: zPrice.nullable(),
    deliverySuburbUuid: z.guid().nullable(),
    discountTotal: zPrice,
    orderItems: z.array(orderItemStrictSchema),
    orderItemsTotal: zPrice,
    payments: z.array(paymentStrictSchema),
    surchargeTotal: zPrice,
    table: z.string().max(3).nullable(),
    total: zPrice,
    uuid: z.guid(),
    customerPhoneNumber: z
      .string()
      .nullable()
      /** customerPhoneNumber - Validate: Checks it's valid local number, eg 0466302332 or 0352438055 */
      .refine(
        (val) => {
          if (!val) return true; // If it's null or '' then continue on to transform step

          // Local format
          if (val.startsWith('0')) {
            return /^0\d{9}$/.test(val); // 0 followed by 9 digits
          }

          // Int format
          const COUNTRY_CODE = '+61';
          if (val.startsWith('+')) {
            return new RegExp(`^\\${COUNTRY_CODE}\\d{9}$`).test(val); // +61 followed by 9 digits
          }
        },
        {
          message: 'Use valid format. Eg 04xxxxxxxx',
        },
      )
      /** customerPhoneNumber - after parsing, convert from local format to int format */
      .transform((val) => {
        // If already in international format or empty
        if (val === null || val.startsWith('+')) {
          return val;
        }

        // If empty string, store null
        if (val === '') return null;

        // Convert to international format
        return `+61${val.substring(1)}`;
      })
      .pipe(z.string().nullable()),
  })
  /** Validate: `isClosed` must only be when status is CANCELLED or COMPLETED */
  .refine(
    (order) => {
      return order.isClosed === isOrderClosed(order.status);
    },
    {
      message: 'order.status is not in sync with order.isClosed.',
      path: ['isClosed'],
    },
  );

// 3. Convex Storage Schema - Database shape
// Server-generated fields become required
export const orderCvxSchema = orderSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
  updatedAt: z.number(),
  version: z.number().min(1).optional(), // Remove .optional() after a Convex migration
  businessDate: z.string().optional(), // Remove .optional() after a Convex migration
  schemaVersion: z.number().int().optional(), // Remove after `m20260904_unsetOrderSchemaVersion` has run
});
export type OrderCvx = z.output<typeof orderCvxSchema>;
assertTypes<NoNewFields<Order, OrderCvx>>();

// Discriminated unions - See https://github.com/colinhacks/zod#discriminated-unions
// Useful when working with forms (eg delivery form).

const onlineOrderFields = {
  customerName: z.string().min(2, { message: 'Name must be at least 2 characters' }),
  customerPhoneNumber: z.string().min(10, { message: 'Mobile must be 10 digits' }),
};
export const orderPlatformSchema = z.discriminatedUnion('platform', [
  // 'WEB' specific validation rules (guest checkout).
  orderStrictSchema.safeExtend({
    ...onlineOrderFields,
    platform: z.literal('CONSUMER_WEB'),
  }),
  // 'IOS' specific validation rules (customer checkout).
  orderStrictSchema.safeExtend({
    ...onlineOrderFields,
    platform: z.literal('CONSUMER_IOS'),
    restaurantCustomerId: z.string(),
  }),
  // 'ANDROID' specific validation rules (customer checkout).
  orderStrictSchema.safeExtend({
    ...onlineOrderFields,
    platform: z.literal('CONSUMER_ANDROID'),
    restaurantCustomerId: z.string(),
  }),

  // 'AI_PHONE' specific validation rules (a guest who rang the venue's AI phone number).
  orderStrictSchema.safeExtend({
    ...onlineOrderFields,
    platform: z.literal('AI_PHONE'),
  }),

  // 'POS' specific validation rules.
  // `localCreatedAt` is set at first local save, before any sync, so it can never be null here.
  // `businessDate`, closure stamping and the archive all key off it.
  orderStrictSchema.safeExtend({
    platform: z.literal('POS'),
    localCreatedAt: z.number(),
  }),
]);
export type OrderPlatform = z.output<typeof orderPlatformSchema>;

export const orderServiceTypeSchema = z.discriminatedUnion('serviceType', [
  // 'DELIVERY' specific validation rules.
  orderStrictSchema.safeExtend({
    deliveryAddress: z.string().min(1),
    deliveryNotes: z.string().nullable().default(null),
    deliverySuburbFee: zPrice.min(0),
    deliverySuburbName: z.string().min(1),
    deliverySuburbPostcode: z.string().min(4).max(4),
    deliverySuburbUuid: z.string().min(1),
    serviceType: z.literal('DELIVERY'),
  }),
  // 'PICKUP' specific validation rules.
  orderStrictSchema.safeExtend({ serviceType: z.literal('PICKUP') }),
  // 'TABLE' specific validation rules.
  orderStrictSchema.safeExtend({ serviceType: z.literal('TABLE') }),
]);
export type OrderServiceType = z.output<typeof orderServiceTypeSchema>;

/** Envelope type wrapping an order with restaurant context. */
export const orderWrapSchema = z.object({
  order: orderCvxSchema,
  restaurantName: z.string(),
  restaurantTimeZone: z.string(),
  restaurantStripeConnectAccountId: z.string().nullish(),
});
export type OrderWrap = z.output<typeof orderWrapSchema>;
